<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useVenueGeocode } from 'dashboard/composables/useVenueGeocode';
import Button from 'dashboard/components-next/button/Button.vue';
import VenueLocationPicker from './VenueLocationPicker.vue';
import VenueBuzzCard from './VenueBuzzCard.vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const props = defineProps({
  venue: {
    type: Object,
    default: null,
  },
  embedded: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'saved']);

const store = useStore();
const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();
const { geocodeLocation } = useVenueGeocode();
const isBuzzEnabled = computed(() => isCloudFeatureEnabled(FEATURE_FLAGS.BUZZ));
const isSubmitting = ref(false);
const name = ref(props.venue?.name || '');
const address = ref(props.venue?.address || '');
const city = ref(props.venue?.city || '');
const country = ref(props.venue?.country || '');
const latitude = ref(props.venue?.latitude ?? null);
const longitude = ref(props.venue?.longitude ?? null);
const bounds = ref(null);
const radiusKm = ref(props.venue?.radius_km ?? null);
const website = ref(props.venue?.website || '');
const googlePlaceId = ref(props.venue?.google_place_id || '');
const instagram = ref(props.venue?.social_profiles?.instagram || '');
const facebook = ref(props.venue?.social_profiles?.facebook || '');
const linkedin = ref(props.venue?.social_profiles?.linkedin || '');

const isEditing = computed(() => !!props.venue);

const title = computed(() =>
  isEditing.value
    ? t('SETTINGS.SPACES.VENUES.EDIT')
    : t('SETTINGS.SPACES.VENUES.CREATE')
);

const containerClass = computed(() =>
  props.embedded
    ? 'w-full flex flex-col gap-4'
    : 'w-[32rem] z-50 min-w-0 absolute top-10 ltr:right-0 rtl:left-0 bg-n-alpha-3 backdrop-blur-[100px] p-6 rounded-xl border border-slate-50 dark:border-slate-900 shadow-md flex flex-col gap-4'
);

const handleClose = () => emit('close');

const mapZoom = computed(() => {
  if (address.value.trim()) return 15;
  if (city.value.trim()) return 12;
  if (country.value.trim()) return 5;
  return 15;
});

const mapZoomBias = computed(() => {
  if (address.value.trim()) return -2;
  if (city.value.trim() || country.value.trim()) return 2;
  return 0;
});

const mapZoomLevel = computed(() => {
  if (address.value.trim()) return 'address';
  if (city.value.trim()) return 'city';
  if (country.value.trim()) return 'country';
  return null;
});

const previewLocation = async () => {
  try {
    const coords = await geocodeLocation({
      address: address.value,
      city: city.value,
      country: country.value,
    });
    if (!coords) return;
    latitude.value = coords.latitude;
    longitude.value = coords.longitude;
    bounds.value = coords.bounds;
  } catch (error) {
    if (error?.code === 'GEOCODE_CANCELED') return;
    useAlert(t('SETTINGS.SPACES.VENUES.GEOCODE_ERROR'));
  }
};

const updateMapLatitude = value => {
  latitude.value = value;
  bounds.value = null;
};

const updateMapLongitude = value => {
  longitude.value = value;
  bounds.value = null;
};

const buildPayload = () => {
  const payload = {
    name: name.value.trim(),
    address: address.value.trim() || null,
    city: city.value.trim() || null,
    country: country.value.trim() || null,
    latitude: latitude.value,
    longitude: longitude.value,
    radius_km: radiusKm.value ? parseInt(radiusKm.value, 10) : null,
  };
  if (!isBuzzEnabled.value) return payload;

  payload.website = website.value.trim() || null;
  payload.google_place_id = googlePlaceId.value.trim() || null;
  payload.social_profiles = {
    instagram: instagram.value.trim() || null,
    facebook: facebook.value.trim() || null,
    linkedin: linkedin.value.trim() || null,
  };
  return payload;
};

const isFormValid = computed(
  () =>
    !!name.value.trim() && !!country.value.trim() && !!city.value.trim()
);

const handleSubmit = async () => {
  if (isSubmitting.value || !isFormValid.value) return;

  isSubmitting.value = true;
  const payload = buildPayload();

  try {
    let response;
    if (isEditing.value) {
      response = await store.dispatch('venues/update', {
        id: props.venue.id,
        ...payload,
      });
      useAlert(t('SETTINGS.SPACES.VENUES.UPDATE_SUCCESS'));
    } else {
      response = await store.dispatch('venues/create', payload);
      useAlert(t('SETTINGS.SPACES.VENUES.CREATE_SUCCESS'));
    }
    emit('saved', response);
    handleClose();
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.message ||
        t('SETTINGS.SPACES.VENUES.SAVE_ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div :class="containerClass" @click.stop>
    <h3 class="text-base font-medium text-slate-900 dark:text-slate-50">
      {{ title }}
    </h3>

    <div>
      <label class="block text-sm font-medium text-n-slate-12 mb-1">
        {{ t('SETTINGS.SPACES.VENUES.NAME') }}
        <span class="text-red-500">*</span>
      </label>
      <input
        v-model="name"
        type="text"
        required
        class="w-full"
        :placeholder="t('SETTINGS.SPACES.VENUES.NAME_PLACEHOLDER')"
        @keyup.enter="handleSubmit"
      />
    </div>

    <div v-if="isBuzzEnabled">
      <label class="block text-sm font-medium text-n-slate-12 mb-1">
        {{ t('SETTINGS.SPACES.VENUES.WEBSITE') }}
      </label>
      <input
        v-model="website"
        type="text"
        class="w-full"
        :placeholder="t('SETTINGS.SPACES.VENUES.WEBSITE_PLACEHOLDER')"
      />
    </div>

    <div v-if="isBuzzEnabled">
      <label class="block text-sm font-medium text-n-slate-12 mb-1">
        {{ t('SETTINGS.SPACES.VENUES.GOOGLE_PLACE_ID') }}
      </label>
      <input
        v-model="googlePlaceId"
        type="text"
        class="w-full"
        :placeholder="t('SETTINGS.SPACES.VENUES.GOOGLE_PLACE_ID_PLACEHOLDER')"
      />
    </div>

    <div v-if="isBuzzEnabled" class="grid grid-cols-1 gap-3">
      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-1">
          {{ t('SETTINGS.SPACES.VENUES.SOCIAL_INSTAGRAM') }}
        </label>
        <input
          v-model="instagram"
          type="text"
          class="w-full"
          :placeholder="
            t('SETTINGS.SPACES.VENUES.SOCIAL_INSTAGRAM_PLACEHOLDER')
          "
        />
      </div>
      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-1">
          {{ t('SETTINGS.SPACES.VENUES.SOCIAL_FACEBOOK') }}
        </label>
        <input
          v-model="facebook"
          type="text"
          class="w-full"
          :placeholder="t('SETTINGS.SPACES.VENUES.SOCIAL_FACEBOOK_PLACEHOLDER')"
        />
      </div>
      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-1">
          {{ t('SETTINGS.SPACES.VENUES.SOCIAL_LINKEDIN') }}
        </label>
        <input
          v-model="linkedin"
          type="text"
          class="w-full"
          :placeholder="t('SETTINGS.SPACES.VENUES.SOCIAL_LINKEDIN_PLACEHOLDER')"
        />
      </div>
    </div>

    <div>
      <label class="block text-sm font-medium text-n-slate-12 mb-1">
        {{ t('SETTINGS.SPACES.VENUES.COUNTRY') }}
        <span class="text-red-500">*</span>
      </label>
      <input
        v-model="country"
        type="text"
        required
        class="w-full"
        :placeholder="t('SETTINGS.SPACES.VENUES.COUNTRY_PLACEHOLDER')"
        @change="previewLocation"
      />
    </div>

    <div>
      <label class="block text-sm font-medium text-n-slate-12 mb-1">
        {{ t('SETTINGS.SPACES.VENUES.CITY') }}
        <span class="text-red-500">*</span>
      </label>
      <input
        v-model="city"
        type="text"
        required
        class="w-full"
        :placeholder="t('SETTINGS.SPACES.VENUES.CITY_PLACEHOLDER')"
        @change="previewLocation"
      />
    </div>

    <div>
      <label class="block text-sm font-medium text-n-slate-12 mb-1">
        {{ t('SETTINGS.SPACES.VENUES.ADDRESS') }}
      </label>
      <input
        v-model="address"
        type="text"
        class="w-full"
        :placeholder="t('SETTINGS.SPACES.VENUES.ADDRESS_PLACEHOLDER')"
        @change="previewLocation"
      />
    </div>

    <div v-if="address.trim() || city.trim() || country.trim() || latitude != null">
      <p class="text-xs text-n-slate-10 mb-2">
        {{ t('SETTINGS.SPACES.VENUES.LOCATION_HINT') }}
      </p>
      <VenueLocationPicker
        :latitude="latitude"
        :longitude="longitude"
        :bounds="bounds"
        :zoom="mapZoom"
        :zoom-bias="mapZoomBias"
        :zoom-level="mapZoomLevel"
        @update:latitude="updateMapLatitude"
        @update:longitude="updateMapLongitude"
      />
    </div>

    <div>
      <label class="block text-sm font-medium text-n-slate-12 mb-1">
        {{ t('SETTINGS.SPACES.VENUES.RADIUS_KM') }}
      </label>
      <input
        v-model="radiusKm"
        type="number"
        class="w-full"
        min="1"
        max="50"
        :placeholder="t('SETTINGS.SPACES.VENUES.RADIUS_KM_PLACEHOLDER')"
      />
    </div>

    <VenueBuzzCard v-if="isEditing && isBuzzEnabled" :venue="venue" />

    <div class="flex gap-2 justify-end">
      <Button
        :label="t('SETTINGS.SPACES.VENUES.CANCEL')"
        slate
        outlined
        md
        @click="handleClose"
      />
      <Button
        :label="
          isEditing
            ? t('SETTINGS.SPACES.VENUES.SAVE')
            : t('SETTINGS.SPACES.VENUES.CREATE')
        "
        md
        :disabled="!isFormValid || isSubmitting"
        :is-loading="isSubmitting"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>
